import React from 'react';
import {
  Platform,
  ScrollView,
  View,
  useWindowDimensions,
  StyleSheet,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import Text from '../../../components/Text';
import Button from '../../../components/Button';
import Heading from '../../../components/Heading';
import HStack from '../../../components/HStack';
import RegisterActionBar from '../../../components/RegisterActionBar';
import RegisterPrevButton from '../../../components/RegisterPrevButton';
import ScreenWrapper from '../components/ScreenWrapper';
import isWeb from '../../../utils/isWeb';
import Contacts from 'react-native-contacts';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../../service/flashMessage';
import { PERMISSIONS, RESULTS, check, request } from 'react-native-permissions';
import openAppSettings from '../../../utils/openAppSettings';
import {showKeyboardAwareAlert} from '../../../utils/keyboard';

const AddContact = ({ navigation }) => {
  const { width } = useWindowDimensions();

  const checkManagementOfficeAccessible = async () => {
    try {
      const contacts = await Contacts.getAll();
      for (let index = 0; index < contacts.length; index++) {
        const element = contacts[index];
        if (
          element.givenName === 'Management' &&
          element.familyName === 'Office'
        ) {
          const hasPhone = element.phoneNumbers && element.phoneNumbers.length > 0;
          const hasEmail = element.emailAddresses && element.emailAddresses.length > 0;
          return { found: true, hasPhone, hasEmail };
        }
      }
      return { found: false, hasPhone: false, hasEmail: false };
    } catch (e) {
      return { found: false, hasPhone: false, hasEmail: false };
    }
  };

  const showLimitedAccessAlert = () => {
    showKeyboardAwareAlert(
      'Limited Contact Access',
      'The Management Office contact was not found in your selected contacts. Please grant full access to contacts so we can verify communication details.',
      [
        {
          text: 'CANCEL',
          onPress: () => {},
        },
        {
          text: 'GRANT FULL ACCESS',
          onPress: () => {
            void openAppSettings('contacts settings');
          },
        },
      ],
    );
  };

  const saveContact = async (isLimitedPermission: boolean = false) => {
    if (isLimitedPermission) {
      const accessCheck = await checkManagementOfficeAccessible();
      if (!accessCheck.found) {
        showLimitedAccessAlert();
        return;
      }
    }
    
    const newContact = {
      emailAddresses: [
        {
          label: 'home',
          email: 'info@communityemail.info',
        },
      ],
      phoneNumbers: [
        {
          label: 'mobile',
          number: '(954) 449-6239',
        },
      ],
      familyName: 'Office',
      givenName: 'Management',
    };

    const contacts = await Contacts.getAll();

    for (let index = 0; index < contacts.length; index++) {
      const element = contacts[index];
      if (
        element.givenName + element.familyName ===
        newContact.givenName + newContact.familyName
      ) {
        navigation.navigate('Password');
        return;
      }
    }

    const contact = await Contacts.addContact(newContact);
    if (contact) {
      showSuccessMessage('Contact added to phone book successfully');
      navigation.navigate('Password');
    }
  };

  const showPermissionDialog = () => {
    showKeyboardAwareAlert(
      'Alert',
      'Please grant Contact permission in settings or skip to continue.',
      [
        {
          text: 'SKIP',
          onPress: () => navigation.navigate('Password'),
        },
        {
          text: 'OK',
          onPress: () => {
            void openAppSettings('contacts settings');
          },
        },
      ],
    );
  };


  const onNext = async () => {
    if (Platform.OS === 'android') {
      try {
        const writePermission = await request(
          PERMISSIONS.ANDROID.WRITE_CONTACTS,
        );
        const readPermission = await request(
          PERMISSIONS.ANDROID.READ_CONTACTS,
        );
        if (writePermission === RESULTS.GRANTED && readPermission === RESULTS.GRANTED) {
          await saveContact(false);
        } else if (
          writePermission === RESULTS.BLOCKED ||
          readPermission === RESULTS.BLOCKED
        ) {
          showPermissionDialog();
        }
      } catch (e) {
        showErrorMessage('AddContact: Permission error' + e);
      }
    }
    if (Platform.OS === 'ios') {
      try {
        const permission = await check(PERMISSIONS.IOS.CONTACTS);
        
        if (permission === RESULTS.DENIED) {
          const requestedPermission = await request(PERMISSIONS.IOS.CONTACTS);
          
          if (requestedPermission === RESULTS.GRANTED) {
            saveContact(false);
          } else if (requestedPermission === RESULTS.LIMITED) {
            saveContact(true);
          } else if (requestedPermission === RESULTS.DENIED || requestedPermission === RESULTS.BLOCKED) {
            showErrorMessage(
              'AddContact - Contact permission denied, please enable in settings',
            );
            showPermissionDialog();
          }
        } else if (permission === RESULTS.GRANTED) {
          saveContact(false);
        } else if (permission === RESULTS.LIMITED) {
          saveContact(true);
        } else if (permission === RESULTS.BLOCKED) {
          showErrorMessage(
            'AddContact - Contact permission blocked, please enable in settings',
          );
          showPermissionDialog();
        }
      } catch (e) {
        showErrorMessage('AddContact - Permission error' + e);
      }
    }
  };

  return (
    <ScreenWrapper alignTop>
      <SafeAreaView
        style={[
          styles.safeArea,
          isWeb && { backgroundColor: '#ffffff', width: width / 3 },
        ]}>
        <RegisterActionBar step={8} totalSteps={10} />
        <ScrollView keyboardShouldPersistTaps="handled">
          <View style={styles.container}>
            <Heading size="lg">Add Contact</Heading>
            <Text style={[styles.text, { fontWeight: '600' }]}>
              Grant this permission so you do not get Unknown Caller from the
              office and block us.
            </Text>
            <Text style={styles.text}>Name: Management Office</Text>
            <Text style={styles.text}>Phone: 954-449-6239</Text>
            <Text style={styles.text}>Email: info@communityemail.info</Text>
            <Text style={styles.text}>
              This app keeps you informed about events and changes. You will
              always receive an app message and an email. In important
              situations, you may also get a text or call.
            </Text>
            <Text style={styles.text}>
              To ensure you receive these messages, we need to add a phone
              number and email to your contacts.
            </Text>
            <Text style={styles.text}>
              We do this so you will always know who is calling or emailing you.
              We will add this contact for you:
            </Text>
            <Text
              style={{
                marginTop: 16,
                fontSize: 20,
                fontWeight: '500'
              }}>
              When prompted, please selected{' '}
              <Text style={{ fontWeight: '600', fontSize: 20, }}>Allow Full Access</Text>
            </Text>
          </View>
        </ScrollView>
        <HStack style={styles.hStack}>
          <RegisterPrevButton />
          <Button onPress={onNext} style={styles.button}>
            Next
          </Button>
        </HStack>
      </SafeAreaView>
    </ScreenWrapper>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    marginVertical: 16,
  },
  container: {
    marginHorizontal: 24,
    marginTop: 16,
  },
  text: {
    marginTop: 16,
    fontSize: 18,
  },
  hStack: {
    margin: 24,
    marginTop: 32,
    marginBottom: 36
  },
  button: {
    flex: 1,
  },
});

export default AddContact;
